import { fetchApi, fetchApiBlob } from '@/services/api-client';

export const LUNCH_DETENTIONS_PATH = '/lunch-detentions';

export type LunchDetentionStatus = 'proposed' | 'approved' | 'rejected' | 'withdrawn' | 'cancelled';
export type LunchDetentionAttendance = 'pending' | 'attended' | 'absent' | 'no_show' | 'excused' | 'suspended';

export type LunchDetentionPerson = {
  id: string;
  displayName: string;
  /** The name on the register. Null for staff, and for a student whose names are the same. */
  fullName: string | null;
  /** Null for staff — they have no grade. */
  gradeLevel: number | null;
  photoUrl: string;
};

export type LunchDetentionDay = {
  id: string;
  detentionId: string;
  date: string;
  attendance: LunchDetentionAttendance;
  markedBy: LunchDetentionPerson | null;
  markedAt: string | null;
  note: string | null;
};

/**
 * A day as returned by `today()` — it has no detention of its own to look up, so the API attaches
 * the context a room list needs (who, whose supervision, and enough of the run to know its phase)
 * directly on each row instead.
 */
export type LunchDetentionRoomDay = LunchDetentionDay & {
  detention: {
    id: string;
    student: LunchDetentionPerson;
    supervisor: LunchDetentionPerson | null;
    status: LunchDetentionStatus;
    startDate: string;
    endDate: string;
    reason: string;
    foldedInCount: number;
    daysLeft: number;
  };
};

/** Another `approved` run for the same student overlapping a detention's dates. */
export type LunchDetentionClash = { id: string; startDate: string; endDate: string };

/**
 * One end of the link between an overlapping request and the run it was folded into. The same
 * shape reads both ways: on the extension it names the run served, and on the run it names each
 * incident that lengthened it.
 */
export type LunchDetentionLink = { id: string; reason: string; startDate: string; endDate: string };

export type LunchDetention = {
  id: string;
  student: LunchDetentionPerson;
  startDate: string;
  endDate: string;
  reason: string;
  status: LunchDetentionStatus;
  proposedBy: LunchDetentionPerson | null;
  decidedBy: LunchDetentionPerson | null;
  decidedAt: string | null;
  decisionNote: string | null;
  supervisor: LunchDetentionPerson | null;
  incidentId: string | null;
  seenByProposerAt: string | null;
  days: LunchDetentionDay[];
  createdAt: string;
  clash: LunchDetentionClash | null;
  /** How many detentions this student has had in this run's school year, this one included. */
  schoolYearRequestCount: number;
  /**
   * The open run this request was folded into. When set, this record owns no days at all — the
   * child serves them on the run named here, which was lengthened by the days this one asked for.
   */
  extends: LunchDetentionLink | null;
  /** The later incidents that lengthened this run. Empty for a run nothing was added to. */
  extendedBy: LunchDetentionLink[];
  documentCount: number;
};

/** A document handed out with a run — usually the exercises the student works on. */
export type LunchDetentionDocument = {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
  uploadedBy: { id: string; displayName: string } | null;
  /** The person who added it, or the SST. */
  canDelete: boolean;
};

export type LunchDetentionDocuments = {
  /** The people named on the run, the student's homeroom teacher and the SST, while the run is live. */
  canUpload: boolean;
  attachments: LunchDetentionDocument[];
};

/** Mirrors the API's `documentUploadOptions`: PDF, Word, Excel, PowerPoint and photos. */
export const LUNCH_DETENTION_DOCUMENT_ACCEPT = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx',
].join(',');

/** One student's clash preview from `GET /lunch-detentions/clash-check`, before anything is submitted. */
export type LunchDetentionClashCheck = {
  studentId: string;
  clash: LunchDetentionClash | null;
  schoolYearRequestCount: number;
};

export type LunchDetentionBadgeCounts = {
  awaitingDecision: number;
  unseenDecisions: number;
  daysToMark: number;
  unreadNotes: number;
};

/** What the MSS tab, its dashboard widget and the bell all count as waiting. `unreadNotes` is
 *  left out: a note is something to read, not a task to clear. */
export function lunchDetentionAttentionCount(badges: LunchDetentionBadgeCounts): number {
  return badges.awaitingDecision + badges.unseenDecisions + badges.daysToMark;
}

export type LunchDetentionFilters = {
  status?: LunchDetentionStatus;
  studentId?: string;
  /** The runs one incident led to. */
  incidentId?: string;
  mine?: boolean;
};

/**
 * Guards against a mis-click that selects half the school, not a limit on real use — mirrors
 * `MAX_STUDENTS_PER_LUNCH_DETENTION_REQUEST` in the API's lunch-detention request DTO. Kept in
 * sync by hand, since the two are separate deployable packages.
 */
export const MAX_STUDENTS_PER_LUNCH_DETENTION_REQUEST = 30;

export type ProposeLunchDetentionData = {
  studentIds: string[];
  startDate: string;
  endDate: string;
  reason: string;
  supervisorId?: string | null;
  incidentId?: string | null;
  /**
   * Students, among `studentIds`, whose record folds into the run they are already serving rather
   * than starting a second one beside it. SST only; the API resolves which run.
   */
  extendStudentIds?: string[];
  /** SST only: record a proposal for the team to decide instead of approving straight away. */
  sendForReview?: boolean;
};

export type DecideLunchDetentionData = {
  approve: boolean;
  decisionNote?: string;
  supervisorId?: string;
  /**
   * Fold this request into a run the student is already serving instead of starting one beside
   * it: that run gains the school days this one asks for, and this record owns none. Approve only.
   */
  extendId?: string;
};

export type UpdateLunchDetentionData = {
  startDate?: string;
  endDate?: string;
  reason?: string;
  supervisorId?: string | null;
};

/** A genuine partial update: a field left out of the object is left untouched server-side. */
export type MarkLunchDetentionDayData = {
  attendance?: LunchDetentionAttendance;
  note?: string | null;
};

export type LunchDetentionNoteVisibility = 'staff' | 'sst_only';

/**
 * One row of a detention's thread. Written notes and what happened to the record share this shape:
 * only notes are stored, the rest is derived on the API from the record, its extensions and its
 * day rows, so a detention created long before the thread existed still shows its whole history.
 */
type LunchDetentionTimelineBase = {
  id: string;
  at: string;
  authorName: string | null;
  body: string | null;
  visibility: LunchDetentionNoteVisibility;
};

export type LunchDetentionTimelineEntry =
  | (LunchDetentionTimelineBase & { kind: 'note' })
  | (LunchDetentionTimelineBase & { kind: 'decision'; status: LunchDetentionStatus })
  | (LunchDetentionTimelineBase & { kind: 'extension'; startDate: string; endDate: string })
  | (LunchDetentionTimelineBase & { kind: 'attendance'; date: string; attendance: LunchDetentionAttendance });

export const lunchDetentionsApi = {
  list: (filters: LunchDetentionFilters = {}) => {
    const qs = new URLSearchParams();
    if (filters.status) qs.append('status', filters.status);
    if (filters.studentId) qs.append('studentId', filters.studentId);
    if (filters.incidentId) qs.append('incidentId', filters.incidentId);
    if (filters.mine) qs.append('mine', '1');
    const s = qs.toString();
    return fetchApi<LunchDetention[]>(`${LUNCH_DETENTIONS_PATH}${s ? `?${s}` : ''}`);
  },
  today: (date?: string) =>
    fetchApi<LunchDetentionRoomDay[]>(
      `${LUNCH_DETENTIONS_PATH}/today${date ? `?date=${encodeURIComponent(date)}` : ''}`
    ),
  unmarked: () => fetchApi<LunchDetentionRoomDay[]>(`${LUNCH_DETENTIONS_PATH}/days/unmarked`),
  badges: () => fetchApi<LunchDetentionBadgeCounts>(`${LUNCH_DETENTIONS_PATH}/badges`),
  get: (id: string) => fetchApi<LunchDetention>(`${LUNCH_DETENTIONS_PATH}/${id}`),
  /** One independent record per student in `studentIds`, in the same order — never merged. */
  propose: (data: ProposeLunchDetentionData) =>
    fetchApi<LunchDetention[]>(LUNCH_DETENTIONS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  /** Preview of the clash/count a submit with these students and dates would produce, without
   *  creating anything — what the propose dialog polls as the teacher picks students and dates. */
  clashCheck: (studentIds: string[], startDate: string, endDate: string) => {
    const qs = new URLSearchParams({ studentIds: studentIds.join(','), startDate, endDate });
    return fetchApi<LunchDetentionClashCheck[]>(`${LUNCH_DETENTIONS_PATH}/clash-check?${qs}`);
  },
  decide: (id: string, data: DecideLunchDetentionData) =>
    fetchApi<LunchDetention>(`${LUNCH_DETENTIONS_PATH}/${id}/decision`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateLunchDetentionData) =>
    fetchApi<LunchDetention>(`${LUNCH_DETENTIONS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  cancel: (id: string) => fetchApi<LunchDetention>(`${LUNCH_DETENTIONS_PATH}/${id}/cancel`, { method: 'POST' }),
  withdraw: (id: string) => fetchApi<LunchDetention>(`${LUNCH_DETENTIONS_PATH}/${id}/withdraw`, { method: 'POST' }),
  notes: (id: string) => fetchApi<LunchDetentionTimelineEntry[]>(`${LUNCH_DETENTIONS_PATH}/${id}/notes`),
  postNote: (id: string, body: string, visibility: LunchDetentionNoteVisibility = 'staff') =>
    fetchApi<LunchDetentionTimelineEntry[]>(`${LUNCH_DETENTIONS_PATH}/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body, visibility }),
    }),
  markNotesRead: (id: string, lane: LunchDetentionNoteVisibility = 'staff') =>
    fetchApi<void>(`${LUNCH_DETENTIONS_PATH}/${id}/notes/read`, {
      method: 'POST',
      body: JSON.stringify({ lane }),
    }),
  markSeen: (id: string) => fetchApi<void>(`${LUNCH_DETENTIONS_PATH}/${id}/seen`, { method: 'POST' }),
  documents: (id: string) => fetchApi<LunchDetentionDocuments>(`${LUNCH_DETENTIONS_PATH}/${id}/attachments`),
  uploadDocuments: (id: string, files: File[]) => {
    const form = new FormData();
    files.forEach(file => form.append('files', file, file.name));
    return fetchApi<LunchDetentionDocuments>(`${LUNCH_DETENTIONS_PATH}/${id}/attachments`, {
      method: 'POST',
      body: form,
      timeoutMs: 120_000,
    });
  },
  deleteDocument: (attachmentId: string) =>
    fetchApi<{ ok: true }>(`${LUNCH_DETENTIONS_PATH}/attachments/${attachmentId}`, { method: 'DELETE' }),
  downloadDocument: (attachmentId: string) => fetchApiBlob(`${LUNCH_DETENTIONS_PATH}/attachments/${attachmentId}`),
  markDay: (dayId: string, data: MarkLunchDetentionDayData) =>
    fetchApi<LunchDetentionDay>(`${LUNCH_DETENTIONS_PATH}/days/${dayId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};
